/**
 * @file: requestErrorDetails.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { HttpError } from './HttpError';

/**
 * The structured payloads the server puts in `details.data` beside a request
 * error's sentence (backend `common/.../RequestViolation.ts`). The sentence
 * (`error.message`) is already localized and names the first field; these say
 * which inputs to mark. Neither ever carries the value the user typed.
 */
export type RequestViolation = {
  /** The path as the API declares it: `vin`, `items.3.memberId`. */
  field: string;
  rule:
    | 'required'
    | 'invalid_format'
    | 'wrong_type'
    | 'too_short'
    | 'too_long'
    | 'out_of_range'
    | 'not_allowed'
    | 'mismatch'
    | 'duplicate';
  limit?: number;
  allowed?: string[];
};

export type DuplicateConflict = {
  /** The fields that make the row unique, as the API names them. */
  fields: string[];
  /** The row it collided with, only when the caller may open it. */
  existing?: { type: string; id: string | number };
};

/** Every input a request error names, in the order the server reported. */
export function requestViolationsOf(error: unknown): RequestViolation[] {
  if (!(error instanceof HttpError)) return [];
  const data = error.details?.data as
    | { violations?: RequestViolation[] }
    | undefined;
  return Array.isArray(data?.violations) ? data!.violations! : [];
}

/** What a write collided with, when the server says. */
export function duplicateConflictOf(
  error: unknown,
): DuplicateConflict | undefined {
  if (!(error instanceof HttpError)) return undefined;
  const data = error.details?.data as
    | { conflict?: DuplicateConflict }
    | undefined;
  return data?.conflict && Array.isArray(data.conflict.fields)
    ? data.conflict
    : undefined;
}
